import type { ArticleBlock } from "@/mocks/articles";

type TocItem = { id: string; text: string };

export const buildToc = (blocks: ArticleBlock[]): TocItem[] => {
  const items: TocItem[] = [];
  let h2Index = 0;
  blocks.forEach((block) => {
    if (block.type === "h2") {
      items.push({ id: `section-${h2Index}`, text: block.text });
      h2Index += 1;
    }
  });
  return items;
};

type ArticleBodyProps = {
  blocks: ArticleBlock[];
  toc: TocItem[];
};

export default function ArticleBody({ blocks, toc }: ArticleBodyProps) {
  let h2Index = -1;

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      const top = el.getBoundingClientRect().top + window.scrollY - 96;
      window.scrollTo({ top, behavior: "smooth" });
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
      <aside className="lg:col-span-3 order-2 lg:order-1">
        <div className="lg:sticky lg:top-28 rounded-lg border border-background-200 bg-background-50 p-5">
          <h2 className="text-sm font-semibold text-foreground-950 flex items-center gap-2">
            <i className="ri-list-check w-4 h-4 flex items-center justify-center text-primary-500"></i>
            文章目录
          </h2>
          <nav className="mt-3" aria-label="文章目录">
            <ul className="space-y-2">
              {toc.map((item, index) => (
                <li key={item.id}>
                  <button
                    type="button"
                    onClick={() => scrollTo(item.id)}
                    className="w-full text-left text-sm text-foreground-600 hover:text-primary-600 transition-colors flex gap-2"
                  >
                    <span className="text-foreground-400 tabular-nums">{String(index + 1).padStart(2, "0")}</span>
                    <span className="leading-relaxed">{item.text}</span>
                  </button>
                </li>
              ))}
            </ul>
          </nav>
        </div>
      </aside>

      <article className="lg:col-span-9 order-1 lg:order-2 max-w-none">
        {blocks.map((block, index) => {
          if (block.type === "h2") {
            h2Index += 1;
            return (
              <h2
                key={index}
                id={`section-${h2Index}`}
                className="scroll-mt-28 mt-10 first:mt-0 text-xl md:text-2xl font-bold text-foreground-950 leading-snug"
              >
                {block.text}
              </h2>
            );
          }
          if (block.type === "h3") {
            return (
              <h3 key={index} className="mt-7 text-base md:text-lg font-semibold text-foreground-900">
                {block.text}
              </h3>
            );
          }
          if (block.type === "quote") {
            return (
              <blockquote
                key={index}
                className="mt-6 pl-5 border-l-4 border-accent-500 bg-accent-50/60 py-4 pr-4 rounded-r-md text-sm md:text-base text-foreground-800 leading-relaxed"
              >
                <i className="ri-double-quotes-l text-accent-500 mr-1"></i>
                {block.text}
              </blockquote>
            );
          }
          if (block.type === "list") {
            return (
              <ul key={index} className="mt-6 space-y-2.5">
                {block.items.map((item) => (
                  <li key={item} className="flex gap-2.5 text-sm md:text-base text-foreground-700 leading-relaxed">
                    <i className="ri-checkbox-circle-fill w-5 h-5 flex items-center justify-center text-secondary-500 mt-0.5 shrink-0"></i>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            );
          }
          return (
            <p key={index} className="mt-5 text-sm md:text-base text-foreground-700 leading-[1.9]">
              {block.text}
            </p>
          );
        })}
      </article>
    </div>
  );
}